import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardContent, CardHeader, CardTitle, Checkbox, Input } from '@mlm/ui';
import {
  getTenantFeatures,
  listProducts,
  patchTenantFeatures,
  type Product,
  type TenantFeatures,
} from '../lib/api';
import { ConfirmSaveButton } from '../components/confirm-save-button';
import { useHasPerm } from '../lib/auth-store';

/**
 * Featured Packages — the admin curates an ORDERED list of real products that
 * the storefront's `featuredPackages` section headlines as cart-able cards.
 * Persisted in tenant_config.features.featured_packages; saving is gated by the
 * grantable `tenant.storefront.write` (tenant admins can edit storefront content).
 * Served to the storefront via GET /storefront/featured-packages. Order =
 * display order. Any product qualifies (not only kits). The section is hidden
 * until this page is enabled AND at least one product is picked.
 */
export function FeaturedPackagesPage() {
  const queryClient = useQueryClient();
  const canWrite = useHasPerm('tenant.storefront.write');
  const featuresQ = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  const productsQ = useQuery({
    queryKey: ['admin-products', 'featured-pick'],
    queryFn: () => listProducts({ pageSize: 200 }),
  });

  const [enabled, setEnabled] = useState(false);
  const [heading, setHeading] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [hydrated, setHydrated] = useState(false);

  // Seed the local draft from the server config once.
  useEffect(() => {
    if (featuresQ.data && !hydrated) {
      setEnabled(featuresQ.data.featured_packages.enabled);
      setHeading(featuresQ.data.featured_packages.heading ?? '');
      setSelectedIds(featuresQ.data.featured_packages.productIds);
      setHydrated(true);
    }
  }, [featuresQ.data, hydrated]);

  const save = useMutation({
    meta: { successMessage: 'Featured packages saved.' },
    mutationFn: (patch: Partial<TenantFeatures>) => patchTenantFeatures(patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tenant-features'] }),
  });

  const products = productsQ.data ?? [];
  const byId = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);
  const selected = selectedIds.map((id) => byId.get(id)).filter((p): p is Product => Boolean(p));

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return products.filter(
      (p) =>
        !selectedIds.includes(p.id) &&
        (term === '' || p.name.toLowerCase().includes(term) || p.sku.toLowerCase().includes(term)),
    );
  }, [products, selectedIds, search]);

  function add(id: string) {
    setSelectedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }
  function removeId(id: string) {
    setSelectedIds((prev) => prev.filter((x) => x !== id));
  }
  function move(idx: number, dir: -1 | 1) {
    setSelectedIds((prev) => {
      const j = idx + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[j]] = [next[j]!, next[idx]!];
      return next;
    });
  }
  const buildConfig = () => ({
    enabled,
    productIds: selectedIds,
    heading: heading.trim() === '' ? null : heading.trim(),
  });

  const errMsg = (save.error as { response?: { data?: { error?: string } } } | null)?.response?.data
    ?.error;

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold">Featured Packages</h1>
        <ConfirmSaveButton
          label="Save"
          title="Save Featured Packages"
          current={featuresQ.data?.featured_packages}
          next={buildConfig()}
          loading={save.isPending}
          disabled={!hydrated || !canWrite}
          onConfirm={() => save.mutateAsync({ featured_packages: buildConfig() })}
        />
      </div>
      {!canWrite && (
        <p className="text-sm text-muted-foreground">
          Read-only — saving Featured Packages requires the storefront-content permission.
        </p>
      )}
      <p className="text-sm text-muted-foreground">
        The ordered products headlined in the storefront's "Featured Packages" section. These are
        real products — the storefront's Add to Cart / Buy Now use them directly. Order = display
        order. The section stays hidden until it's enabled here and at least one product is picked;
        place or reorder it on the storefront from Layout &amp; Theme. To stop these products from
        also appearing in the product grid further down the home page, turn on "Leave Featured
        Packages out of the product grid" in Tenant Settings → Storefront home.
      </p>
      {save.isSuccess && !save.isPending && <p className="text-sm text-success">Saved.</p>}
      {errMsg && <p className="text-sm text-destructive">{errMsg}</p>}

      <Card>
        <CardContent className="space-y-3 pt-6">
          <label className="flex items-center gap-2 text-sm font-medium">
            <Checkbox checked={enabled} onCheckedChange={(c) => setEnabled(c === true)} />
            Show the Featured Packages section on the storefront
          </label>
          <div className="space-y-1">
            <label className="text-sm font-medium">Section heading (optional)</label>
            <Input
              placeholder="Featured Packages"
              value={heading}
              maxLength={120}
              onChange={(e) => setHeading(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Leave blank to use the section's default title.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Selected (ordered) */}
        <Card>
          <CardHeader>
            <CardTitle>Packages ({selected.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {selected.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No packages selected. Add some from the list →
              </p>
            ) : (
              <ul className="divide-y">
                {selected.map((p, i) => (
                  <li key={p.id} className="flex items-center gap-2 py-2">
                    <span className="w-5 text-xs text-muted-foreground">{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{p.name}</div>
                      <div className="font-mono text-xs text-muted-foreground">
                        {p.sku} · ₱{Number(p.price).toLocaleString()}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => move(i, -1)}
                      disabled={i === 0}
                    >
                      ↑
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => move(i, 1)}
                      disabled={i === selected.length - 1}
                    >
                      ↓
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => removeId(p.id)}>
                      Remove
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Picker */}
        <Card>
          <CardHeader>
            <CardTitle>Add products</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input
              placeholder="Search by name or SKU…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {productsQ.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {products.length === 0 ? 'No products yet — add products first.' : 'No matches.'}
              </p>
            ) : (
              <ul className="max-h-80 divide-y overflow-y-auto">
                {filtered.map((p) => (
                  <li key={p.id} className="flex items-center gap-2 py-2">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm">{p.name}</div>
                      <div className="font-mono text-xs text-muted-foreground">
                        {p.sku} · ₱{Number(p.price).toLocaleString()}
                        {!p.active && ' · inactive'}
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => add(p.id)}>
                      Add
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
